import { useEffect } from 'react'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import ContentSection from '../../components/UI/ContentSection'
import PageTitle from '../../components/UI/PageTitle'
import InputWrapper from '../../components/Formik/InputWrapper'
import Label from '../../components/Formik/Label'
import Input from '../../components/Formik/Input'
import Button from '../../components/Buttons/Button'
import Spinner from '../../components/UI/Spinner'
import useSendSupportMessage from '../../services/support/useSendSupportMessage'
import { useAuthContext } from '../../context/auth/auth'

const Support = () => {
  const { t } = useTranslation()
  const { authClient } = useAuthContext()
  const { mutate, isLoading } = useSendSupportMessage()

  useEffect(() => {
    document.title = `${t('support')} - ReportAll`
  }, [t])

  // Mirrors the minimum lengths mail.serializers.ContactUsSerializer enforces,
  // so the form fails here instead of on a round trip.
  const validationSchema = yup.object().shape({
    firstName: yup.string().required(t('requiredField')),
    lastName: yup.string().required(t('requiredField')),
    email: yup.string().email(t('invalidEmail')).required(t('requiredField')),
    subject: yup
      .string()
      .trim()
      .min(5, t('subjectMustBeAtLeast5Characters'))
      .required(t('requiredField')),
    message: yup
      .string()
      .trim()
      .min(10, t('messageMustBeAtLeast10Characters'))
      .required(t('requiredField')),
  })

  const formik = useFormik({
    initialValues: {
      firstName: authClient?.first_name ?? '',
      lastName: authClient?.last_name ?? '',
      email: authClient?.email ?? '',
      subject: '',
      message: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      mutate(
        {
          full_name: `${values.firstName} ${values.lastName}`.trim(),
          email: values.email,
          subject: values.subject.trim(),
          body: values.message.trim(),
          recipient_type: 'SUPPORT',
        },
        {
          onSuccess: () =>
            resetForm({ values: { ...values, subject: '', message: '' } }),
        },
      )
    },
  })

  const hasError = (field: keyof typeof formik.values) =>
    Boolean(formik.touched[field] && formik.errors[field])

  return (
    <ContentSection>
      <PageTitle title={t('howCanWeHelpYou')} />

      <h2 className='mt-6 text-2xl font-bold text-[#3D3D3D]'>
        {t('supportFormTitle')}
      </h2>
      <p className='mt-1 text-lightGray'>{t('supportFormDescription')}</p>

      <form
        onSubmit={formik.handleSubmit}
        className='bg-white rounded-20px shadow-form mt-7 p-5 sm:p-10 max-w-[52.9375rem]'
      >
        <div className='grid gap-5 sm:grid-cols-2'>
          <InputWrapper width='w-full'>
            <Label text={`${t('firstName')}*`} />
            <Input
              as='input'
              type='text'
              placeholder={t('value')}
              hasError={hasError('firstName')}
              {...formik.getFieldProps('firstName')}
            />
          </InputWrapper>
          <InputWrapper width='w-full'>
            <Label text={`${t('lastName')}*`} />
            <Input
              as='input'
              type='text'
              placeholder={t('value')}
              hasError={hasError('lastName')}
              {...formik.getFieldProps('lastName')}
            />
          </InputWrapper>
        </div>

        <InputWrapper width='w-full'>
          <div className='mt-5'>
            <Label text={`${t('email')}*`} />
          </div>
          <Input
            as='input'
            type='email'
            placeholder={t('mailAddress')}
            hasError={hasError('email')}
            {...formik.getFieldProps('email')}
          />
        </InputWrapper>

        <InputWrapper width='w-full'>
          <div className='mt-5'>
            <Label text={`${t('subject')}*`} />
          </div>
          <Input
            as='input'
            type='text'
            placeholder={t('supportSubjectPlaceholder')}
            hasError={hasError('subject')}
            {...formik.getFieldProps('subject')}
          />
        </InputWrapper>

        <InputWrapper width='w-full'>
          <div className='mt-5'>
            <Label text={`${t('supportMessageLabel')}*`} />
          </div>
          <textarea
            rows={5}
            placeholder={t('yourMessageHere')}
            className={`${
              hasError('message') ? 'border-red-500' : 'border-borderGray'
            } bg-white rounded-lg px-4 py-3 max-w-full border-[1px] border-solid focus:border-primaryBlue hover:border-primaryBlue transition-all outline-none placeholder:text-lightGray resize-y`}
            {...formik.getFieldProps('message')}
          />
        </InputWrapper>

        <hr className='m-auto w-full border-[#E2E2E2] mt-7' />

        <div className='flex justify-end mt-5'>
          <Button type='submit' text={t('sendMessage')} disabled={isLoading}>
            {isLoading && <Spinner spinner='form' />}
          </Button>
        </div>
      </form>
    </ContentSection>
  )
}

export default Support
